import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';

const MandalaRing = ({ size, duration, opacity, delay }) => (
  <motion.div
    animate={{ rotate: 360 }}
    transition={{ duration, repeat: Infinity, ease: 'linear', delay }}
    style={{
      position: 'absolute',
      width: size,
      height: size,
      borderRadius: '50%',
      border: `1px solid rgba(201,168,76,${opacity})`,
      top: '50%',
      left: '50%',
      transform: 'translate(-50%,-50%)',
      pointerEvents: 'none',
    }}
  />
);

const Particle = ({ x, y, delay }) => (
  <motion.div
    initial={{ opacity: 0, y: 0 }}
    animate={{ opacity: [0, 1, 0], y: -80 }}
    transition={{ duration: 3, delay, repeat: Infinity, ease: 'easeOut' }}
    style={{
      position: 'absolute',
      left: `${x}%`,
      bottom: `${y}%`,
      width: 3,
      height: 3,
      borderRadius: '50%',
      background: '#C9A84C',
      pointerEvents: 'none',
    }}
  />
);

export default function Hero() {
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const heroRef = useRef(null);

  useEffect(() => {
    const onMove = (e) => {
      if (!heroRef.current) return;
      const rect = heroRef.current.getBoundingClientRect();
      setMousePos({
        x: (e.clientX - rect.left - rect.width / 2) / rect.width,
        y: (e.clientY - rect.top - rect.height / 2) / rect.height,
      });
    };
    window.addEventListener('mousemove', onMove);
    return () => window.removeEventListener('mousemove', onMove);
  }, []);

  const particles = Array.from({ length: 12 }, (_, i) => ({
    x: 5 + i * 8,
    y: Math.random() * 30,
    delay: i * 0.4,
  }));

  return (
    <section
      id="home"
      ref={heroRef}
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        overflow: 'hidden',
        background: 'radial-gradient(ellipse at 50% 50%, #1a1008 0%, #0D0D0D 70%)',
      }}
    >
      {/* Parallax background grid */}
      <motion.div
        style={{
          position: 'absolute', inset: 0,
          backgroundImage: `
            linear-gradient(rgba(201,168,76,0.04) 1px, transparent 1px),
            linear-gradient(90deg, rgba(201,168,76,0.04) 1px, transparent 1px)
          `,
          backgroundSize: '60px 60px',
          x: mousePos.x * -15,
          y: mousePos.y * -15,
        }}
      />

      {/* Mandala rings */}
      <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <MandalaRing size={280} duration={40} opacity={0.12} delay={0} />
        <MandalaRing size={420} duration={60} opacity={0.08} delay={5} />
        <MandalaRing size={560} duration={80} opacity={0.06} delay={10} />
        <MandalaRing size={700} duration={100} opacity={0.04} delay={15} />
        <MandalaRing size={850} duration={120} opacity={0.03} delay={20} />

        {/* Inner geometric */}
        <motion.svg
          animate={{ rotate: -360 }}
          transition={{ duration: 50, repeat: Infinity, ease: 'linear' }}
          width="240" height="240"
          style={{ position: 'absolute' }}
          viewBox="0 0 240 240"
        >
          {[0,45,90,135].map(angle => (
            <line
              key={angle}
              x1="120" y1="10"
              x2="120" y2="230"
              stroke="rgba(201,168,76,0.12)"
              strokeWidth="1"
              transform={`rotate(${angle} 120 120)`}
            />
          ))}
          {[0,30,60,90,120,150].map(angle => (
            <line
              key={angle}
              x1="10" y1="120"
              x2="230" y2="120"
              stroke="rgba(232,115,42,0.08)"
              strokeWidth="1"
              transform={`rotate(${angle} 120 120)`}
            />
          ))}
          <polygon
            points="120,20 220,170 20,170"
            fill="none"
            stroke="rgba(201,168,76,0.15)"
            strokeWidth="1"
          />
          <polygon
            points="120,220 20,70 220,70"
            fill="none"
            stroke="rgba(201,168,76,0.15)"
            strokeWidth="1"
          />
        </motion.svg>

        {/* Central glow */}
        <div style={{
          position: 'absolute',
          width: 200,
          height: 200,
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(232,115,42,0.12) 0%, transparent 70%)',
        }} />
      </div>

      {/* Floating particles */}
      {particles.map((p, i) => <Particle key={i} {...p} />)}

      {/* Main content */}
      <div style={{ position: 'relative', zIndex: 10, textAlign: 'center', padding: '0 20px', maxWidth: 900 }}>

        {/* Sanskrit badge */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.8 }}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 12,
            background: 'rgba(201,168,76,0.08)',
            border: '1px solid rgba(201,168,76,0.25)',
            borderRadius: 40,
            padding: '8px 24px',
            marginBottom: 40,
          }}
        >
          <span style={{ fontSize: 20, color: '#C9A84C' }}>ॐ</span>
          <span style={{ fontFamily: 'Cinzel', fontSize: 11, letterSpacing: 3, color: '#C9A84C', textTransform: 'uppercase' }}>
            Sri Arunachala Services Initiative
          </span>
          <span style={{ fontSize: 20, color: '#C9A84C' }}>ॐ</span>
        </motion.div>

        {/* Headline */}
        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.9 }}
          style={{
            fontFamily: 'Cinzel',
            fontSize: 'clamp(2.8rem, 7vw, 5.5rem)',
            fontWeight: 700,
            lineHeight: 1.1,
            letterSpacing: 2,
            marginBottom: 24,
            background: 'linear-gradient(135deg, #F5F0E8 0%, #C9A84C 50%, #E8732A 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}
        >
          VEDIC IT
        </motion.h1>

        <motion.div
          initial={{ opacity: 0, scaleX: 0 }}
          animate={{ opacity: 1, scaleX: 1 }}
          transition={{ delay: 0.6, duration: 0.8 }}
          style={{
            width: 120,
            height: 2,
            background: 'linear-gradient(90deg, transparent, #C9A84C, transparent)',
            margin: '0 auto 32px',
          }}
        />

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7, duration: 0.8 }}
          style={{
            fontFamily: 'Playfair Display',
            fontSize: 'clamp(1.1rem, 2.5vw, 1.6rem)',
            color: '#B0A898',
            lineHeight: 1.7,
            marginBottom: 20,
            fontStyle: 'italic',
          }}
        >
          Blending Ancient Wisdom with Cutting-Edge IT Solutions
        </motion.p>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.9, duration: 0.8 }}
          style={{
            fontFamily: 'Cinzel',
            fontSize: 'clamp(0.75rem, 1.5vw, 0.95rem)',
            letterSpacing: 4,
            color: '#E8732A',
            textTransform: 'uppercase',
            marginBottom: 52,
          }}
        >
          We Build the Clarity Before We Build the Code
        </motion.p>

        {/* CTA Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.1, duration: 0.7 }}
          style={{ display: 'flex', gap: 16, justifyContent: 'center', flexWrap: 'wrap' }}
        >
          <motion.button
            whileHover={{ scale: 1.05, boxShadow: '0 0 30px rgba(232,115,42,0.5)' }}
            whileTap={{ scale: 0.97 }}
            onClick={() => document.getElementById('services')?.scrollIntoView({ behavior: 'smooth' })}
            style={{
              background: 'linear-gradient(135deg, #E8732A, #C4581A)',
              border: 'none',
              borderRadius: 4,
              padding: '16px 40px',
              color: 'white',
              fontFamily: 'Inter',
              fontSize: 13,
              letterSpacing: 2,
              textTransform: 'uppercase',
              cursor: 'pointer',
              fontWeight: 600,
            }}
          >
            Explore Services
          </motion.button>
          <motion.button
            whileHover={{ scale: 1.05, borderColor: 'rgba(201,168,76,0.8)' }}
            whileTap={{ scale: 0.97 }}
            onClick={() => document.getElementById('about')?.scrollIntoView({ behavior: 'smooth' })}
            style={{
              background: 'transparent',
              border: '1px solid rgba(201,168,76,0.35)',
              borderRadius: 4,
              padding: '16px 40px',
              color: '#C9A84C',
              fontFamily: 'Inter',
              fontSize: 13,
              letterSpacing: 2,
              textTransform: 'uppercase',
              cursor: 'pointer',
              fontWeight: 500,
              transition: 'border-color 0.3s',
            }}
          >
            Our Story
          </motion.button>
        </motion.div>

        {/* Stats row */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.4, duration: 0.8 }}
          style={{
            marginTop: 80,
            display: 'flex',
            gap: 60,
            justifyContent: 'center',
            flexWrap: 'wrap',
          }}
        >
          {[
            { num: '80G', label: 'Registered Non-Profit' },
            { num: '6+', label: 'Core Services' },
            { num: '∞', label: 'Wisdom Driven Impact' },
          ].map((stat, i) => (
            <div key={i} style={{ textAlign: 'center' }}>
              <div style={{ fontFamily: 'Cinzel', fontSize: 32, color: '#C9A84C', lineHeight: 1 }}>{stat.num}</div>
              <div style={{ fontSize: 11, letterSpacing: 2, color: '#6B6560', textTransform: 'uppercase', marginTop: 6 }}>{stat.label}</div>
            </div>
          ))}
        </motion.div>
      </div>

      {/* Scroll indicator */}
      <motion.div
        animate={{ y: [0, 10, 0] }}
        transition={{ duration: 2, repeat: Infinity }}
        style={{
          position: 'absolute',
          bottom: 40,
          left: '50%',
          transform: 'translateX(-50%)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 8,
          cursor: 'pointer',
        }}
        onClick={() => document.getElementById('about')?.scrollIntoView({ behavior: 'smooth' })}
      >
        <span style={{ fontSize: 10, letterSpacing: 3, color: '#6B6560', textTransform: 'uppercase' }}>Scroll</span>
        <div style={{ width: 1, height: 40, background: 'linear-gradient(to bottom, #6B6560, transparent)' }} />
      </motion.div>
    </section>
  );
}
